import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Typography } from '@mui/material'
import { throttle } from 'lodash'

import FlameExtinguished from 'assets/images/flameExtinguished.svg'

import FlameIgnited, { GameName } from './FlameIgnited'
import { styles } from './IdeaFlameMotivator.styles'

export const TEST_ID = 'IdeaFlameMotivator'

interface MotivationCopyProps {
	ideaRange: string
	motivationCopy: string
}

const defaultFlameColor = '#1976d2'

const MotivationCopy = ({
	ideaCount,
	motivationCopy,
	highlight,
	shadow,
	flameColor,
}: MotivationCopyProps & {
	flameColor?: string
	highlight?: boolean
	ideaCount?: number
	shadow?: boolean
}) => (
	<Box sx={styles.copyContainer}>
		<Typography
			sx={{
				...styles.ideaCount,
				...(highlight
					? styles.highlightedIdeaCount(
							flameColor ?? defaultFlameColor
						)
					: {}),
			}}
			className={highlight ? 'highlight' : ''}
			variant="body1"
			data-testid={`${TEST_ID}__idea-count`}
		>
			{ideaCount}
		</Typography>{' '}
		<Typography
			sx={{ ...styles.ideaCount }}
			data-testid={`${TEST_ID}__idea-label`}
		>
			&nbsp;{ideaCount === 1 ? 'IDEA' : 'IDEAS'}&nbsp;
		</Typography>
		<Typography
			sx={{
				...styles.copy,
				...(shadow
					? styles.motivationCopyShadow(
							flameColor ?? defaultFlameColor
						)
					: {}),
			}}
			data-testid={`${TEST_ID}__motivation-copy`}
		>
			{motivationCopy}
		</Typography>
	</Box>
)

type MotivatonLevel = 0 | 1 | 2 | 3
type MotivationCopies = {
	[index in MotivatonLevel]: ReactElement
}

const motivationCopies: MotivationCopies = {
	0: (
		<MotivationCopy
			ideaRange="0"
			motivationCopy="Add your first idea above"
		/>
	),
	1: (
		<MotivationCopy
			ideaRange="1 - 5"
			motivationCopy="Nice! What other ideas can you document?"
		/>
	),
	2: (
		<MotivationCopy
			ideaRange="6 - 10"
			motivationCopy="Now you're on a roll - don't stop now!"
		/>
	),
	3: (
		<MotivationCopy
			ideaRange="+11"
			motivationCopy="You're an innovation superstar"
		/>
	),
}

const levelFromCount = (n: number): MotivatonLevel => {
	if (n <= 0) {
		return 0
	}
	if (n <= 5) {
		return 1
	}
	if (n <= 10) {
		return 2
	}
	return 3
}
interface FlameProps {
	light: boolean
	variant: GameName
}
const Flame = ({ light, variant }: FlameProps) => {
	if (!light) {
		return (
			<img
				alt="Flame extinguished"
				src={FlameExtinguished as unknown as string}
				style={styles.flame}
				data-testid={`${TEST_ID}__flame-extinguished`}
			/>
		)
	} else {
		return (
			<Box sx={styles.flame} data-testid={`${TEST_ID}__flame-ignited`}>
				<FlameIgnited variant={variant} title="Flame ignited" />
			</Box>
		)
	}
}

interface IdeaFlameMotivatorProps {
	ideaCount: number
	variant: GameName
}
const IdeaFlameMotivator = ({
	ideaCount,
	variant,
}: IdeaFlameMotivatorProps) => {
	const level = levelFromCount(ideaCount)
	const [prevLevel, setPrevLevel] = useState(level)
	const [shadow, setShadow] = useState(false)
	const [prevCount, setPrevCount] = useState(ideaCount)
	const [highlight, setHighlight] = useState(false)

	const flameColor = (() => {
		const variants = {
			DRESS_UP: '#12b9f1',
			SIMON_SAYS: '#7523f9',
			BATTLE_SHIPS: '#f9d423',
			LEAP_FROG: '#d1f2a7',
			ROUND_ROBIN: '#fc6767',
			MUSICAL_CHAIRS: '#90dae3',
			SLOT_MACHINE: '#ccc',
			CONNECT_FOUR: '#ccc',
			TEETER_TOTTER: '#ffa1bd',
			OPPOSITE_DAY: '#bde2ff',
			BUILDING_BLOCKS: '#ff8a9f',
		}
		return variants[variant] || '#1976d2'
	})()

	useEffect(() => {
		if (level !== prevLevel) {
			setShadow(true)
			setTimeout(() => setShadow(false), 1000)
			setPrevLevel(level)
		}
		setPrevLevel(level)
	}, [level, prevLevel])

	useEffect(() => {
		if (ideaCount > prevCount) {
			setHighlight(true)
			setTimeout(() => setHighlight(false), 1000)
			setPrevCount(ideaCount)
		}
		setPrevCount(ideaCount)
	}, [ideaCount, prevCount])

	const motivationCopy = useMemo(
		() => (
			<MotivationCopy
				{...motivationCopies[level].props}
				highlight={highlight}
				shadow={shadow}
				ideaCount={ideaCount}
				flameColor={flameColor}
			/>
		),
		[level, highlight, shadow, ideaCount, flameColor]
	)

	const flames = [1, 2, 3].map((flamePos) => (
		<Flame
			light={level >= flamePos}
			variant={variant}
			key={`flame-${flamePos}`}
		/>
	))
	return (
		<Box sx={styles.motivator} data-testid={TEST_ID}>
			{flames}
			{motivationCopy}
		</Box>
	)
}

export const richTextToIdeas = (htmlString: string): number => {
	const container = document.createElement('div')
	container.innerHTML = htmlString

	const nonEmptyParagraphs = Array.from(
		container.querySelectorAll('p')
	).filter((p) => (p.textContent ?? '').trim().length !== 0)

	return nonEmptyParagraphs.length
}

export const useRichTextToIdeas = () => {
	const throttledFn = useMemo(() => throttle(richTextToIdeas, 500), [])
	return useCallback(
		(htmlString: string) => {
			return throttledFn(htmlString) ?? 0
		},
		[throttledFn]
	)
}

export default IdeaFlameMotivator
